/**
 * TileGrid — grid of PV string/inverter tiles with kW values.
 *
 * States: active (blue), idle (gray), fault (red), selected (soft blue + outline)
 */

import { useState } from 'react';
import './TileGrid.css';

export type TileStatus = 'active' | 'idle' | 'fault';

export interface TileData {
  id: string;
  label: string;   // e.g. "1.3"
  power: number;   // kW
  status: TileStatus;
}

interface TileGridProps {
  tiles?: TileData[];
  onTileSelect?: (tile: TileData | null) => void;
  selectedId?: string;
}

// Generate mock tiles: 24 tiles, 2 faults, 4 idle, rest active
function generateMockTiles(): TileData[] {
  const tiles: TileData[] = [];
  for (let inv = 1; inv <= 4; inv++) {
    for (let str = 1; str <= 6; str++) {
      const label = `${inv}.${str}`;
      const idx = (inv - 1) * 6 + (str - 1);
      let status: TileStatus = 'active';
      if (idx === 5 || idx === 14) status = 'fault';
      else if (idx === 2 || idx === 9 || idx === 17 || idx === 21) status = 'idle';

      tiles.push({
        id: `str-${inv}-${str}`,
        label,
        power: status === 'fault' ? 0 : status === 'idle' ? 0 : +(Math.random() * 0.4 + 0.7).toFixed(2),
        status,
      });
    }
  }
  return tiles;
}

const MOCK_TILES = generateMockTiles();

export default function TileGrid({ tiles = MOCK_TILES, onTileSelect, selectedId }: TileGridProps) {
  const [selected, setSelected] = useState<string | null>(selectedId ?? null);

  const handleClick = (tile: TileData) => {
    const next = selected === tile.id ? null : tile.id;
    setSelected(next);
    onTileSelect?.(next ? tile : null);
  };

  return (
    <div className="tile-grid-wrapper">
      <div className="tile-grid" role="grid" aria-label="PV string tile grid">
        {tiles.map((tile) => {
          const isSelected = selected === tile.id;
          const cls = [
            'pv-tile',
            `pv-tile--${tile.status}`,
            isSelected ? 'pv-tile--selected' : '',
          ].filter(Boolean).join(' ');

          return (
            <button
              key={tile.id}
              type="button"
              role="gridcell"
              className={cls}
              onClick={() => handleClick(tile)}
              title={`${tile.id}: ${tile.power.toFixed(2)} kW — ${tile.status}`}
            >
              {tile.status === 'fault' ? (
                <>
                  <span className="pv-tile__fault-icon">!</span>
                  <span className="pv-tile__unit">FAULT</span>
                  <span className="pv-tile__label">{tile.label}</span>
                </>
              ) : (
                <>
                  <span className="pv-tile__value tabular">
                    {tile.status === 'idle' ? '—' : tile.power.toFixed(2)}
                  </span>
                  <span className="pv-tile__unit">kW</span>
                  <span className="pv-tile__label">{tile.label}</span>
                </>
              )}
            </button>
          );
        })}
      </div>

      {/* Legend */}
      <div className="tile-legend">
        <span className="tile-legend-item tile-legend-item--active">Active</span>
        <span className="tile-legend-item tile-legend-item--idle">Idle</span>
        <span className="tile-legend-item tile-legend-item--fault">Fault</span>
        <span className="tile-legend-item tile-legend-item--selected">Selected</span>
      </div>
    </div>
  );
}
